import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Typography,
  TextField,
  Button,
  Box,
  Grid,
  CircularProgress,
  Card,
  CardContent,
  Stack,
  Tabs,
  Tab,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import mcpService, { Mcp, MCP_TIPOS } from '../../services/mcpService';
import grupoMcpService from '../../services/grupoMcpService';
import type { GrupoMcp } from '../../services/grupoMcpService';
import carteraService from '../../services/carteraService';
import type { Cartera } from '../../services/carteraService';
import GrupoMcpSearch from '../../components/GrupoMcpSearch';
import CarteraSearch from '../../components/CarteraSearch';
import SearchableSelect from '../../components/SearchableSelect';

/** Códigos de tipo según `mcpService.MCP_TIPOS` */
const MCP_TIPO_RETENCION = 'R';
const MCP_TIPO_OTROS = 'O';

const RETENCION_RADIO_PROVEEDORES = [
  { value: 'SUSS', label: 'SUSS' },
  { value: 'IB', label: 'IB' },
  { value: 'Ganancias', label: 'Ganancias' },
  { value: 'IVA', label: 'IVA' },
  { value: 'Otras Ret', label: 'Otras Ret' },
] as const;

const RETENCION_RADIO_CLIENTES = [
  { value: 'SUSS', label: 'SUSS' },
  { value: 'IB', label: 'IB' },
  { value: 'Ganancias', label: 'Ganancias' },
  { value: 'IVA', label: 'IVA' },
] as const;

interface McpFormState {
  tipo: string;
  mcp: string;
  descripcion: string;
  clase: string;
  cartera: string;
  grupo: string;
  nroCuenta: string;
  nroSuc: string;
  nroComprobante: string;
  montoSujeto: string;
  montoRetencion: string;
  porcentaje: string;
  idImpuestoAfip: string;
  idRegulacion: string;
  idEscala: string;
  idRetencion: string;
  montoSujetoMax: string;
  montoRetencionMax: string;
  reporteRpt: string;
  codRetIg: string;
  montoMinNoImpon: string;
  porcNoInsc: string;
  porcPersHumSucInd: string;
  idJurisdiccion: string;
  idRegimen: string;
}

const emptyForm: McpFormState = {
  tipo: 'E',
  mcp: '',
  descripcion: '',
  clase: '',
  cartera: '',
  grupo: '',
  nroCuenta: '',
  nroSuc: '',
  nroComprobante: '',
  montoSujeto: '',
  montoRetencion: '',
  porcentaje: '',
  idImpuestoAfip: '',
  idRegulacion: '',
  idEscala: '',
  idRetencion: '',
  montoSujetoMax: '',
  montoRetencionMax: '',
  reporteRpt: '',
  codRetIg: '',
  montoMinNoImpon: '',
  porcNoInsc: '',
  porcPersHumSucInd: '',
  idJurisdiccion: '',
  idRegimen: '',
};

const numOrNull = (s: string): number | null => {
  const t = s.trim();
  if (t === '') return null;
  const n = Number(t.replace(',', '.'));
  return Number.isFinite(n) ? n : null;
};

const shortOrNull = (s: string): number | null => {
  const n = numOrNull(s);
  if (n === null) return null;
  return Math.trunc(n);
};

const intOrNull = (s: string): number | null => {
  const n = numOrNull(s);
  if (n === null) return null;
  return Math.trunc(n);
};

const toForm = (dto: Mcp): McpFormState => ({
  tipo: (dto.tipo || '').trim(),
  mcp: dto.mcp || '',
  descripcion: dto.descripcion ?? '',
  clase: dto.clase ?? '',
  cartera: dto.cartera != null ? String(dto.cartera) : '',
  grupo: dto.grupo != null ? String(dto.grupo) : '',
  nroCuenta: dto.nroCuenta != null ? String(dto.nroCuenta) : '',
  nroSuc: dto.nroSuc != null ? String(dto.nroSuc) : '',
  nroComprobante: dto.nroComprobante != null ? String(dto.nroComprobante) : '',
  montoSujeto: dto.montoSujeto != null ? String(dto.montoSujeto) : '',
  montoRetencion: dto.montoRetencion != null ? String(dto.montoRetencion) : '',
  porcentaje: dto.porcentaje != null ? String(dto.porcentaje) : '',
  idImpuestoAfip: dto.idImpuestoAfip ?? '',
  idRegulacion: dto.idRegulacion ?? '',
  idEscala: dto.idEscala ?? '',
  idRetencion: dto.idRetencion ?? '',
  montoSujetoMax: dto.montoSujetoMax != null ? String(dto.montoSujetoMax) : '',
  montoRetencionMax: dto.montoRetencionMax != null ? String(dto.montoRetencionMax) : '',
  reporteRpt: dto.reporteRpt ?? '',
  codRetIg: dto.codRetIg ?? '',
  montoMinNoImpon: dto.montoMinNoImpon != null ? String(dto.montoMinNoImpon) : '',
  porcNoInsc: dto.porcNoInsc != null ? String(dto.porcNoInsc) : '',
  porcPersHumSucInd: dto.porcPersHumSucInd != null ? String(dto.porcPersHumSucInd) : '',
  idJurisdiccion: dto.idJurisdiccion ?? '',
  idRegimen: dto.idRegimen ?? '',
});

const toPayload = (f: McpFormState): Mcp => ({
  tipo: f.tipo.trim(),
  mcp: f.mcp.trim(),
  descripcion: f.descripcion.trim() || null,
  clase: f.clase.trim() || null,
  cartera: shortOrNull(f.cartera),
  grupo: f.grupo.trim() === '' ? null : shortOrNull(f.grupo),
  nroCuenta: f.tipo.trim() === MCP_TIPO_RETENCION ? intOrNull(f.nroCuenta) : null,
  nroSuc: shortOrNull(f.nroSuc),
  nroComprobante: intOrNull(f.nroComprobante),
  montoSujeto: numOrNull(f.montoSujeto),
  montoRetencion: numOrNull(f.montoRetencion),
  porcentaje: numOrNull(f.porcentaje),
  idImpuestoAfip: f.idImpuestoAfip.trim() || null,
  idRegulacion: f.idRegulacion.trim() || null,
  idEscala: f.idEscala.trim() || null,
  idRetencion: f.idRetencion.trim() || null,
  montoSujetoMax: numOrNull(f.montoSujetoMax),
  montoRetencionMax: numOrNull(f.montoRetencionMax),
  reporteRpt: f.reporteRpt.trim() || null,
  codRetIg: f.codRetIg.trim() || null,
  montoMinNoImpon: numOrNull(f.montoMinNoImpon),
  porcNoInsc: numOrNull(f.porcNoInsc),
  porcPersHumSucInd: numOrNull(f.porcPersHumSucInd),
  idJurisdiccion: f.idJurisdiccion.trim() || null,
  idRegimen: f.idRegimen.trim() || null,
});

const McpForm: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { mcp: mcpParam, tipo: tipoParam } = useParams<{ mcp: string; tipo: string }>();
  const isEdit = Boolean(mcpParam && tipoParam);

  const [formData, setFormData] = useState<McpFormState>({ ...emptyForm });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Lookup state
  const [grupoSearchOpen, setGrupoSearchOpen] = useState(false);
  const [carteraSearchOpen, setCarteraSearchOpen] = useState(false);
  const [grupoLabel, setGrupoLabel] = useState('');
  const [carteraLabel, setCarteraLabel] = useState('');
  const [activeTab, setActiveTab] = useState(0);

  const isTipoRetencion = formData.tipo === MCP_TIPO_RETENCION;
  const isTipoOtros = formData.tipo === MCP_TIPO_OTROS;

  useEffect(() => {
    if (activeTab === 1 && !isTipoRetencion) setActiveTab(0);
    if (activeTab === 2 && !isTipoOtros) setActiveTab(0);
  }, [activeTab, isTipoRetencion, isTipoOtros]);

  const load = useCallback(async () => {
    if (!mcpParam || !tipoParam) return;
    try {
      setLoading(true);
      const dto = await mcpService.getMcp(mcpParam, tipoParam);
      setFormData(toForm(dto));

      if (dto.grupo) {
        try {
          const g = await grupoMcpService.getGrupoMcp(dto.grupo);
          setGrupoLabel(`${g.grupoMcp} - ${g.descripcion || ''}`);
        } catch {
          setGrupoLabel(String(dto.grupo));
        }
      }
      if (dto.cartera) {
        try {
          const c = await carteraService.getCartera(dto.cartera);
          setCarteraLabel(`${c.nroBanco} - ${c.nombreBanco?.trim() || ''}`);
        } catch {
          setCarteraLabel(String(dto.cartera));
        }
      }
    } catch {
      enqueueSnackbar('Error al cargar el medio MCP', { variant: 'error' });
      navigate('/tesoreria/medios-mcp');
    } finally {
      setLoading(false);
    }
  }, [mcpParam, tipoParam, enqueueSnackbar, navigate]);

  useEffect(() => {
    if (isEdit) load();
    else {
      setFormData({ ...emptyForm });
      setGrupoLabel('');
      setCarteraLabel('');
    }
  }, [isEdit, load]);

  const handleChange =
    (name: keyof McpFormState) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | { target: { value: string } }) => {
      setFormData((prev) => ({ ...prev, [name]: e.target.value }));
    };

  const handleGrupoSelect = (gm: GrupoMcp) => {
    setFormData((prev) => ({ ...prev, grupo: String(gm.grupoMcp) }));
    setGrupoLabel(`${gm.grupoMcp} - ${gm.descripcion || ''}`);
    setGrupoSearchOpen(false);
  };

  const handleCarteraSelect = (c: Cartera) => {
    setFormData((prev) => ({ ...prev, cartera: String(c.nroBanco) }));
    setCarteraLabel(`${c.nroBanco} - ${c.nombreBanco?.trim() || ''}`);
    setCarteraSearchOpen(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.mcp.trim()) {
      enqueueSnackbar('Ingrese el código MCP', { variant: 'warning' });
      return;
    }
    if (!formData.tipo.trim()) {
      enqueueSnackbar('Seleccione el tipo', { variant: 'warning' });
      return;
    }
    if (!formData.grupo.trim()) {
      enqueueSnackbar('Seleccione el grupo de caja', { variant: 'warning' });
      return;
    }
    if (!formData.cartera.trim()) {
      enqueueSnackbar('Seleccione la cartera', { variant: 'warning' });
      return;
    }
    const payload = toPayload(formData);

    try {
      setSaving(true);
      if (isEdit && mcpParam && tipoParam) {
        await mcpService.updateMcp(mcpParam, tipoParam, payload);
        enqueueSnackbar('Medio MCP actualizado', { variant: 'success' });
      } else {
        await mcpService.createMcp(payload);
        enqueueSnackbar('Medio MCP creado', { variant: 'success' });
      }
      navigate('/tesoreria/medios-mcp');
    } catch (error: any) {
      const data = error?.response?.data;
      if (data?.errors && typeof data.errors === 'object') {
        let detalle = '';
        for (const campo of Object.keys(data.errors)) {
          if (Array.isArray(data.errors[campo])) {
            detalle += `${campo}: ${data.errors[campo].join('; ')}\n`;
          }
        }
        if (detalle) {
          enqueueSnackbar(`Error en los datos: ${detalle}`, { variant: 'error', autoHideDuration: 12000 });
          return;
        }
      }
      const msg = (data?.message || data?.title || error?.message || 'Error al guardar').toString();
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  const fieldSx = {
    '& .MuiOutlinedInput-root': { borderRadius: 2 },
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: '#1976d2' }}>
            {isEdit ? 'Editar medio MCP' : 'Nuevo medio MCP'}
          </Typography>

          <form onSubmit={handleSubmit}>
            <Tabs
              value={activeTab}
              onChange={(_e, v) => setActiveTab(v)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                borderBottom: 1,
                borderColor: 'divider',
                mb: 2,
                '& .MuiTab-root': { textTransform: 'none', fontWeight: 500 },
              }}
            >
              <Tab label="General" id="mcp-tab-0" aria-controls="mcp-tabpanel-0" />
              <Tab label="Retención a proveedores" disabled={!isTipoRetencion} id="mcp-tab-1" aria-controls="mcp-tabpanel-1" />
              <Tab label="Otros / Retención de clientes" disabled={!isTipoOtros} id="mcp-tab-2" aria-controls="mcp-tabpanel-2" />
            </Tabs>

            <Box role="tabpanel" hidden={activeTab !== 0} id="mcp-tabpanel-0" aria-labelledby="mcp-tab-0" sx={{ display: activeTab === 0 ? 'block' : 'none' }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Código MCP"
                    value={formData.mcp}
                    onChange={handleChange('mcp')}
                    fullWidth
                    required
                    disabled={isEdit}
                    sx={fieldSx}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <SearchableSelect
                    label="Tipo"
                    value={formData.tipo || null}
                    onChange={(val) => {
                      const tipo = val ?? '';
                      setFormData((p) => ({
                        ...p,
                        tipo,
                        nroCuenta: tipo === MCP_TIPO_RETENCION ? p.nroCuenta : '',
                      }));
                      setActiveTab(0);
                    }}
                    options={MCP_TIPOS.map((t) => ({ value: t.value, label: t.label }))}
                    required
                    disabled={isEdit}
                    sx={fieldSx}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField label="Descripción" value={formData.descripcion} onChange={handleChange('descripcion')} fullWidth sx={fieldSx} />
                </Grid>

                <Grid item xs={12} md={4}>
                  <Stack direction="row" spacing={1}>
                    <TextField
                      label="Grupo caja"
                      value={grupoLabel || formData.grupo}
                      fullWidth
                      required
                      disabled
                      sx={fieldSx}
                      placeholder="Busque y seleccione un grupo"
                    />
                    <Button
                      variant="outlined"
                      onClick={() => setGrupoSearchOpen(true)}
                      sx={{ minWidth: 100, borderRadius: 2, textTransform: 'none' }}
                    >
                      Buscar
                    </Button>
                  </Stack>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Stack direction="row" spacing={1}>
                    <TextField
                      label="Cartera"
                      value={carteraLabel || formData.cartera}
                      fullWidth
                      required
                      disabled
                      sx={fieldSx}
                      placeholder="Busque y seleccione una cartera"
                    />
                    <Button
                      variant="outlined"
                      onClick={() => setCarteraSearchOpen(true)}
                      sx={{ minWidth: 100, borderRadius: 2, textTransform: 'none' }}
                    >
                      Buscar
                    </Button>
                  </Stack>
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    label="Nro Cuenta Cheque Pend. Acred."
                    type="number"
                    value={formData.nroCuenta}
                    onChange={handleChange('nroCuenta')}
                    fullWidth
                    disabled={!isTipoRetencion}
                    sx={fieldSx}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label="Clase"
                    value={formData.clase}
                    onChange={handleChange('clase')}
                    fullWidth
                    sx={fieldSx}
                  />
                </Grid>
              </Grid>
            </Box>

            <Box role="tabpanel" hidden={activeTab !== 1} id="mcp-tabpanel-1" aria-labelledby="mcp-tab-1" sx={{ display: activeTab === 1 ? 'block' : 'none' }}>
              {!isTipoRetencion ? null : (
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'flex-start' }}>
                      <TextField
                        label="Nº sucursal"
                        type="number"
                        value={formData.nroSuc}
                        onChange={handleChange('nroSuc')}
                        sx={{ ...fieldSx, width: { xs: '100%', sm: 160 } }}
                      />
                      <TextField
                        label="Nº comprobante"
                        type="number"
                        value={formData.nroComprobante}
                        onChange={handleChange('nroComprobante')}
                        fullWidth
                        sx={fieldSx}
                      />
                    </Stack>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <TextField label="Monto sujeto" value={formData.montoSujeto} onChange={handleChange('montoSujeto')} fullWidth sx={fieldSx} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField label="Monto retención" value={formData.montoRetencion} onChange={handleChange('montoRetencion')} fullWidth sx={fieldSx} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField label="Porcentaje" value={formData.porcentaje} onChange={handleChange('porcentaje')} fullWidth sx={fieldSx} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField label="Monto sujeto máx." value={formData.montoSujetoMax} onChange={handleChange('montoSujetoMax')} fullWidth sx={fieldSx} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField label="Monto retención máx." value={formData.montoRetencionMax} onChange={handleChange('montoRetencionMax')} fullWidth sx={fieldSx} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField label="Monto mín. no imponible" value={formData.montoMinNoImpon} onChange={handleChange('montoMinNoImpon')} fullWidth sx={fieldSx} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField label="% no inscripto" value={formData.porcNoInsc} onChange={handleChange('porcNoInsc')} fullWidth sx={fieldSx} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField label="% pers. hum. suc. ind." value={formData.porcPersHumSucInd} onChange={handleChange('porcPersHumSucInd')} fullWidth sx={fieldSx} />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField label="Id escala" value={formData.idEscala} onChange={handleChange('idEscala')} fullWidth sx={fieldSx} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField label="Id impuesto AFIP" value={formData.idImpuestoAfip} onChange={handleChange('idImpuestoAfip')} fullWidth sx={fieldSx} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField label="Id regulación" value={formData.idRegulacion} onChange={handleChange('idRegulacion')} fullWidth sx={fieldSx} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField label="Código ret. IG" value={formData.codRetIg} onChange={handleChange('codRetIg')} fullWidth sx={fieldSx} />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField label="Ubicación del reporte (RPT)" value={formData.reporteRpt} onChange={handleChange('reporteRpt')} fullWidth sx={fieldSx} />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl component="fieldset" variant="standard" sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 2, width: '100%' }}>
                      <FormLabel component="legend" sx={{ fontWeight: 600, mb: 1 }}>
                        Retención
                      </FormLabel>
                      <RadioGroup
                        value={
                          RETENCION_RADIO_PROVEEDORES.some((o) => o.value === formData.idRetencion)
                            ? formData.idRetencion
                            : ''
                        }
                        onChange={(e) => setFormData((p) => ({ ...p, idRetencion: e.target.value }))}
                      >
                        {RETENCION_RADIO_PROVEEDORES.map((opt) => (
                          <FormControlLabel key={opt.value} value={opt.value} control={<Radio size="small" />} label={opt.label} />
                        ))}
                      </RadioGroup>
                      <TextField
                        label="Id retención (otro)"
                        value={
                          RETENCION_RADIO_PROVEEDORES.some((o) => o.value === formData.idRetencion) ? '' : formData.idRetencion
                        }
                        onChange={(e) => setFormData((p) => ({ ...p, idRetencion: e.target.value }))}
                        fullWidth
                        size="small"
                        placeholder="Si no está en la lista"
                        sx={{ ...fieldSx, mt: 1 }}
                      />
                    </FormControl>
                  </Grid>
                </Grid>
              )}
            </Box>

            <Box role="tabpanel" hidden={activeTab !== 2} id="mcp-tabpanel-2" aria-labelledby="mcp-tab-2" sx={{ display: activeTab === 2 ? 'block' : 'none' }}>
              {!isTipoOtros ? null : (
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField label="Id impuesto AFIP" value={formData.idImpuestoAfip} onChange={handleChange('idImpuestoAfip')} fullWidth sx={fieldSx} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField label="Id regulación" value={formData.idRegulacion} onChange={handleChange('idRegulacion')} fullWidth sx={fieldSx} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField label="Id jurisdicción" value={formData.idJurisdiccion} onChange={handleChange('idJurisdiccion')} fullWidth sx={fieldSx} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField label="Id régimen" value={formData.idRegimen} onChange={handleChange('idRegimen')} fullWidth sx={fieldSx} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl component="fieldset" variant="standard" sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 2, width: '100%' }}>
                      <FormLabel component="legend" sx={{ fontWeight: 600, mb: 1 }}>
                        Retención
                      </FormLabel>
                      <RadioGroup
                        value={
                          RETENCION_RADIO_CLIENTES.some((o) => o.value === formData.idRetencion) ? formData.idRetencion : ''
                        }
                        onChange={(e) => setFormData((p) => ({ ...p, idRetencion: e.target.value }))}
                      >
                        {RETENCION_RADIO_CLIENTES.map((opt) => (
                          <FormControlLabel key={opt.value} value={opt.value} control={<Radio size="small" />} label={opt.label} />
                        ))}
                      </RadioGroup>
                      <TextField
                        label="Id retención (otro)"
                        value={RETENCION_RADIO_CLIENTES.some((o) => o.value === formData.idRetencion) ? '' : formData.idRetencion}
                        onChange={(e) => setFormData((p) => ({ ...p, idRetencion: e.target.value }))}
                        fullWidth
                        size="small"
                        placeholder="Si no está en la lista"
                        sx={{ ...fieldSx, mt: 1 }}
                      />
                    </FormControl>
                  </Grid>
                </Grid>
              )}
            </Box>

            <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={saving}
                sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none', fontWeight: 500 }}
              >
                {saving ? <CircularProgress size={24} /> : 'Guardar'}
              </Button>
              <Button variant="outlined" onClick={() => navigate('/tesoreria/medios-mcp')} disabled={saving} sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none' }}>
                Cancelar
              </Button>
            </Stack>
          </form>
        </CardContent>
      </Card>

      <GrupoMcpSearch
        open={grupoSearchOpen}
        onClose={() => setGrupoSearchOpen(false)}
        onSelect={handleGrupoSelect}
      />
      <CarteraSearch
        open={carteraSearchOpen}
        onClose={() => setCarteraSearchOpen(false)}
        onSelect={handleCarteraSelect}
      />
    </Box>
  );
};

export default McpForm;
